import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { Calendar, Clock, Users, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManageBooking = () => {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const [reservation, setReservation] = useState(null);
  const [guest, setGuest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [selectedTime, setSelectedTime] = useState("");

  useEffect(() => {
    fetchReservation();
  }, [token]);

  const fetchReservation = async () => {
    try {
      const response = await axios.get(`${API}/reservations/manage/${token}`);
      setReservation(response.data.reservation);
      setGuest(response.data.guest);
      setSelectedTime(response.data.reservation.time);
      if (response.data.guest?.language) {
        i18n.changeLanguage(response.data.guest.language);
      }
    } catch (error) {
      console.error("Error fetching reservation:", error);
      toast.error("Reservation not found");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableTimes = async () => {
    try {
      const response = await axios.post(`${API}/availability`, {
        date: reservation.date,
        party_size: reservation.party_size
      });
      setAvailableTimes(response.data.available_times || []);
    } catch (error) {
      console.error("Error fetching available times:", error);
      toast.error("Error loading available times");
    }
  };

  const handleEditTime = () => {
    setEditMode(true);
    fetchAvailableTimes();
  };

  const handleUpdateTime = async () => {
    if (!selectedTime) {
      toast.error("Selecteer een nieuwe tijd");
      return;
    }

    try {
      await axios.put(`${API}/reservations/${reservation.id}`, {
        time: selectedTime
      });
      
      toast.success("Reservering bijgewerkt");
      setReservation({ ...reservation, time: selectedTime });
      setEditMode(false);
    } catch (error) {
      console.error("Error updating reservation:", error);
      toast.error("Fout bij bijwerken reservering");
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(t('manage.cancel_confirm'))) return;

    try {
      await axios.delete(`${API}/reservations/${reservation.id}`);
      toast.success(t('manage.cancelled'));
      setReservation({...reservation, status: "cancelled"});
    } catch (error) {
      console.error("Error cancelling reservation:", error);
      toast.error("Error cancelling reservation");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-brand-beige">Loading...</div>
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-brand-gold mx-auto mb-4" />
          <h2 className="text-2xl font-serif text-brand-beige">Reservation not found</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-void py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-[#1A1A1A] border border-brand-gold/30 p-8">
          <h1 className="text-3xl sm:text-4xl font-serif text-brand-beige mb-6" data-testid="manage-title">
            {t('manage.title')}
          </h1>

          <div className="space-y-6">
            <div className="bg-[#222222] p-6 border-l-3 border-brand-gold">
              <h2 className="text-xl font-serif text-brand-gold mb-4" data-testid="details-title">
                {t('manage.details')}
              </h2>
              
              <div className="space-y-3 text-gray-300">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-brand-gold" />
                  <span data-testid="guest-name">{reservation.guest_name || guest?.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-brand-gold" />
                  <span data-testid="reservation-date">{reservation.date}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-brand-gold" />
                  <span data-testid="reservation-time">{reservation.time}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-brand-gold" />
                  <span data-testid="party-size">{reservation.party_size} guests</span>
                </div>
              </div>

              {reservation.special_requests && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="text-xs uppercase tracking-wide text-brand-gold mb-1">Special Requests</p>
                  <p className="text-gray-300" data-testid="special-requests-display">{reservation.special_requests}</p>
                </div>
              )}

              {reservation.allergies && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="text-xs uppercase tracking-wide text-brand-gold mb-1">Allergies</p>
                  <p className="text-gray-300" data-testid="allergies-display">{reservation.allergies}</p>
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-xs uppercase tracking-wide text-brand-gold mb-1">Status</p>
                <span 
                  className={`inline-block px-3 py-1 text-sm font-semibold ${
                    reservation.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                    reservation.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}
                  data-testid="reservation-status"
                >
                  {reservation.status}
                </span>
              </div>
            </div>

            {reservation.status === 'confirmed' && (
              <div className="space-y-3">
                {!editMode ? (
                  <>
                    <Button
                      onClick={handleEditTime}
                      className="w-full bg-brand-gold text-black hover:bg-[#F0C74A]"
                      data-testid="edit-time-button"
                    >
                      {t('manage.modify')}
                    </Button>
                    <Button
                      onClick={handleCancel}
                      variant="destructive"
                      className="w-full"
                      data-testid="cancel-reservation-button"
                    >
                      {t('manage.cancel')}
                    </Button>
                  </>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-serif text-brand-gold mb-3">Selecteer nieuwe tijd</h3>
                      {availableTimes.length === 0 ? (
                        <p className="text-gray-400 text-sm">Geen beschikbare tijden</p>
                      ) : (
                        <div className="grid grid-cols-3 gap-2">
                          {availableTimes.map(time => (
                            <button
                              key={time}
                              onClick={() => setSelectedTime(time)}
                              className={`py-2 px-3 border transition-colors ${
                                selectedTime === time
                                  ? 'bg-brand-gold text-black border-brand-gold'
                                  : 'border-white/20 text-gray-300 hover:border-brand-gold/50'
                              }`}
                              data-testid={`time-option-${time.replace(':', '-')}`}
                            >
                              {time}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleUpdateTime}
                        className="flex-1 bg-brand-gold text-black"
                        data-testid="confirm-time-change"
                      >
                        Bevestig wijziging
                      </Button>
                      <Button
                        onClick={() => {
                          setEditMode(false);
                          setSelectedTime(reservation.time);
                        }}
                        variant="outline"
                        className="flex-1"
                      >
                        Annuleer
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageBooking;